import { useState } from 'react';
import { signIn, confirmSignIn } from 'aws-amplify/auth';
import {
  phoneLookup,
  phoneRegister,
  phoneVerifyRegister,
  phoneResend,
  lookupSupervisorCode,
} from '../../services/api';

// Custom phone SMS-OTP auth for the dashboard (Amplify's <Authenticator> can't
// do passwordless phone OTP). Login uses Cognito CUSTOM_AUTH via the functional
// signIn/confirmSignIn APIs; registration hits our backend endpoints. On a
// successful login, Amplify's Hub 'signedIn' event flips useAuthenticator's
// authStatus and the app redirects — no explicit navigation needed here.

type Mode = 'login' | 'register';
type Step = 'form' | 'otp';

const only10 = (s: string) => s.replace(/\D/g, '').slice(0, 10);

export default function PhoneAuthPanel({ mode }: { mode: Mode }) {
  const [step, setStep] = useState<Step>('form');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // shared
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [username, setUsername] = useState(''); // login: resolved email

  // register
  const [displayName, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [supervisorCode, setSupervisorCode] = useState('');

  const reset = () => { setError(null); setInfo(null); };

  const startLogin = async () => {
    reset();
    if (!/^\d{10}$/.test(phone)) return setError('Enter a valid 10-digit number');
    setBusy(true);
    try {
      const res = await phoneLookup(phone);
      if (!res.exists || !res.username) { setError('No account found for this phone number.'); return; }
      setUsername(res.username);
      await signIn({ username: res.username, options: { authFlowType: 'CUSTOM_WITHOUT_SRP' } });
      setStep('otp');
      setInfo('Code sent by SMS.');
    } catch (e) {
      setError((e as Error).message || 'Could not start login');
    } finally { setBusy(false); }
  };

  const startRegister = async () => {
    reset();
    if (!displayName.trim()) return setError('Name is required');
    if (!/^[^@]+@[^@]+\.[^@]+$/.test(email)) return setError('Enter a valid email');
    if (!/^\d{10}$/.test(phone)) return setError('Enter a valid 10-digit number');
    if (password.length < 8) return setError('Password must be at least 8 characters');
    if (!supervisorCode.trim()) return setError('Supervisor code is required');
    setBusy(true);
    try {
      const sup = await lookupSupervisorCode(supervisorCode.trim().toUpperCase());
      if (!sup?.supervisorId) { setError('Invalid supervisor code.'); return; }
      await phoneRegister({ displayName: displayName.trim(), email: email.trim(), phone, password, supervisorId: sup.supervisorId });
      setStep('otp');
      setInfo('Code sent by SMS.');
    } catch (e) {
      setError((e as Error).message || 'Registration failed');
    } finally { setBusy(false); }
  };

  const verify = async () => {
    reset();
    if (code.length !== 6) return setError('Enter the 6-digit code');
    setBusy(true);
    try {
      if (mode === 'login') {
        await confirmSignIn({ challengeResponse: code });
        // authStatus flips via Hub → LoginRedirect navigates.
      } else {
        await phoneVerifyRegister(phone, code);
        setStep('form');
        setInfo('Registered! Your account is pending admin approval. You can sign in once approved.');
      }
    } catch (e) {
      setError((e as Error).message || 'Verification failed');
    } finally { setBusy(false); }
  };

  const resend = async () => {
    reset();
    setBusy(true);
    try {
      if (mode === 'login') {
        await signIn({ username, options: { authFlowType: 'CUSTOM_WITHOUT_SRP' } });
      } else {
        await phoneResend(phone);
      }
      setInfo('Code re-sent.');
    } catch (e) {
      setError((e as Error).message || 'Could not resend code');
    } finally { setBusy(false); }
  };

  const field = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-violet-500 focus:ring-1 focus:ring-violet-500 outline-none';

  return (
    <div className="space-y-3">
      {error && <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2">{error}</div>}
      {info && <div className="rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm px-3 py-2">{info}</div>}

      {step === 'form' && mode === 'login' && (
        <>
          <input className={field} placeholder="Mobile number (10 digits)" inputMode="numeric"
                 value={phone} onChange={(e) => setPhone(only10(e.target.value))} />
          <button className="btn-primary w-full" disabled={busy} onClick={startLogin}>
            {busy ? 'Sending…' : 'Send OTP'}
          </button>
        </>
      )}

      {step === 'form' && mode === 'register' && (
        <>
          <input className={field} placeholder="Full name" value={displayName} onChange={(e) => setName(e.target.value)} />
          <input className={field} placeholder="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input className={field} placeholder="Mobile number (10 digits)" inputMode="numeric"
                 value={phone} onChange={(e) => setPhone(only10(e.target.value))} />
          <input className={field} placeholder="Password (min 8 chars)" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <input className={field} placeholder="Supervisor code" value={supervisorCode} onChange={(e) => setSupervisorCode(e.target.value)} />
          <button className="btn-primary w-full" disabled={busy} onClick={startRegister}>
            {busy ? 'Sending…' : 'Register & Send OTP'}
          </button>
        </>
      )}

      {step === 'otp' && (
        <>
          <p className="text-sm text-gray-500">Enter the 6-digit code sent to +91 {phone}.</p>
          <input className={`${field} text-center tracking-[0.5em] text-lg`} placeholder="------" inputMode="numeric"
                 value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} />
          <button className="btn-primary w-full" disabled={busy} onClick={verify}>
            {busy ? 'Verifying…' : 'Verify'}
          </button>
          <button className="text-sm text-violet-600 hover:text-violet-800 w-full" disabled={busy} onClick={resend}>
            Resend code
          </button>
        </>
      )}
    </div>
  );
}
